<template>
  <div class="followup-panel">
    <!-- 标题栏 -->
    <div class="followup-header">
      <div class="header-title">
        <span>超时跟进</span>
        <!-- 规则说明（悬浮展示，避免大段文案挤占面板） -->
        <el-tooltip placement="bottom-start" effect="light" :open-delay="200">
          <div slot="content" class="followup-help-box">
            <p>· 以我方最后一条消息的发送时间为准，粉丝超时未回复才触发</p>
            <p>· 粉丝回复后重新计时；每轮未回复仅跟进一次</p>
            <p>· 保存后从当前时间起生效，不会补发历史未回复会话</p>
            <p>· 消息按顺序发送（支持文字/语音/视频/小程序），发送失败不重试</p>
          </div>
          <i class="el-icon-question help-icon"></i>
        </el-tooltip>
      </div>
    </div>

    <el-scrollbar class="followup-scroll">
      <div class="followup-body">
        <!-- 触发条件 -->
        <div class="config-card">
          <div class="section-title">
            <span class="title-bar"></span>
            <span class="title-text">触发条件</span>
          </div>
          <div class="condition-line">
            粉丝超过
            <el-input-number
              v-model="form.intervalMinutes"
              :min="1"
              :max="1440"
              size="small"
              controls-position="right"
              class="interval-input"
            ></el-input-number>
            分钟未回复，将自动发送以下消息
          </div>
        </div>

        <!-- 回复内容 -->
        <div class="config-card">
          <div class="section-title">
            <span class="title-bar"></span>
            <span class="title-text">回复内容</span>
            <span v-if="form.messages.length" class="title-hint">共 {{ form.messages.length }} 条</span>
            <span v-if="form.messages.length" class="edit-script-link" @click="editorVisible = true">
              <i class="el-icon-edit"></i>编辑话术
            </span>
          </div>
          <div class="msg-list-box">
            <div v-if="form.messages.length === 0" class="msg-empty">
              <i class="el-icon-chat-line-round"></i>
              <div class="empty-text">暂未配置回复消息</div>
              <el-button size="mini" type="primary" plain @click="editorVisible = true">立即配置</el-button>
            </div>
            <div v-for="(m, i) in form.messages" :key="i" class="msg-row">
              <span class="msg-seq">{{ i + 1 }}</span>
              <i :class="msgIcon(m)" class="msg-type-icon"></i>
              <span class="msg-preview" :title="msgPreview(m, true)">{{ msgPreview(m) }}</span>
            </div>
          </div>
        </div>

        <!-- 操作区：启用开关（即时保存）+ 保存按钮 -->
        <div class="followup-actions">
          <div class="enable-switch">
            <span class="enable-label">启用超时跟进</span>
            <el-switch v-model="form.enabled" size="small" :disabled="saving" @change="onToggleEnabled"></el-switch>
          </div>
          <el-button size="small" type="primary" :loading="saving" @click="save">保 存</el-button>
        </div>
        <div v-if="dirty" class="dirty-tip">
          <i class="el-icon-warning-outline"></i> 配置有修改，点击保存后生效
        </div>
      </div>
    </el-scrollbar>

    <!-- 消息序列编辑器（与自动回复共用的共享组件） -->
    <auto-reply-messages-editor
      :visible.sync="editorVisible"
      :messages="form.messages"
      @confirm="onEditorConfirm"
    />
  </div>
</template>

<script>
import { saveAutoFollowUpConfig } from '../../api/autoFollowUp.js'
import { msgIcon, msgPreview } from '../../utils/autoreplyMessages.js'
import AutoReplyMessagesEditor from './AutoReplyMessagesEditor.vue'

// 超时跟进设置面板：启用开关（即时保存）+ 间隔分钟数 + 消息序列（编辑器与自动回复共用）。
// 保存即刷新服务端生效起点（enabledAt）：仅对保存之后产生的"我方最后一句"生效
export default {
  name: 'FollowUpPanel',
  components: { AutoReplyMessagesEditor },
  data() {
    return {
      form: { enabled: false, intervalMinutes: 10, messages: [] },
      saving: false,
      editorVisible: false
    }
  },
  computed: {
    // 面板配置与已保存配置是否有差异（enabled 由开关即时保存，此处主要反映间隔/话术修改）
    dirty() {
      const saved = this.chatStore.followUpConfig
      if (!saved) return false
      const savedMsgs = Array.isArray(saved.messages) ? saved.messages : []
      return (this.form.intervalMinutes || 10) !== (saved.intervalMinutes || 10) ||
        JSON.stringify(this.form.messages) !== JSON.stringify(savedMsgs)
    }
  },
  created() {
    this.loadConfig()
  },
  methods: {
    msgIcon,
    msgPreview,
    loadConfig() {
      // 优先取全局 store（Home 已加载）；未就绪时兜底拉取一次
      const storeConfig = this.chatStore.followUpConfig
      if (storeConfig) {
        this.applyConfig(storeConfig)
        return
      }
      this.chatStore.loadFollowUpConfig().then(() => {
        this.applyConfig(this.chatStore.followUpConfig)
      }).catch(() => {})
    },
    applyConfig(data) {
      if (!data) return
      this.form.enabled = !!data.enabled
      this.form.intervalMinutes = data.intervalMinutes || 10
      this.form.messages = Array.isArray(data.messages) ? data.messages : []
    },
    onEditorConfirm(messages) {
      this.form.messages = messages
      this.editorVisible = false
    },
    // 启用开关即时生效：拨动即保存；校验失败/请求失败时回滚开关到持久化状态
    onToggleEnabled(val) {
      const saved = this.chatStore.followUpConfig
      // 与已保存状态一致（如回滚触发的重复事件）无需处理
      if (val === !!(saved && saved.enabled)) return
      this.save().then((ok) => {
        if (!ok) {
          this.form.enabled = !!(this.chatStore.followUpConfig && this.chatStore.followUpConfig.enabled)
        }
      })
    },
    // 保存：返回 Promise<boolean>，供开关回滚判断（保存按钮忽略返回值）
    save() {
      if (this.saving) return Promise.resolve(false)
      if (this.form.enabled && (!this.form.messages || this.form.messages.length === 0)) {
        this.$message.warning('启用前请先配置回复消息')
        return Promise.resolve(false)
      }
      this.saving = true
      return saveAutoFollowUpConfig({
        enabled: this.form.enabled,
        intervalMinutes: this.form.intervalMinutes || 10,
        messages: this.form.messages
      }).then((data) => {
        this.$message.success('已保存，从当前时间起对新产生的未回复生效')
        if (data) {
          this.applyConfig(data)
          this.chatStore.setFollowUpConfig(data)
        }
        return true
      }).catch(() => {
        this.$message.error('保存失败')
        return false
      }).finally(() => {
        this.saving = false
      })
    }
  }
}
</script>

<style lang="scss" scoped>
// 规则说明（el-tooltip 弹层内容）
.followup-help-box {
  width: 330px;
  font-size: 12px;
  line-height: 1.8;
  color: #606266;

  p {
    margin: 0;
  }
}

.followup-panel {
  height: 100%;
  display: flex;
  flex-direction: column;

  .followup-header {
    height: 50px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 0 15px;
    font-weight: bold;
    color: #333;
    border-bottom: 1px solid #e6e6e6;
    background: #fff;

    .header-title {
      display: flex;
      align-items: center;

      .help-icon {
        margin-left: 6px;
        font-size: 15px;
        font-weight: normal;
        color: #909399;
        cursor: pointer;

        &:hover {
          color: #409EFF;
        }
      }
    }
  }

  .followup-scroll {
    flex: 1;
    padding: 10px;
  }

  .followup-body {
    // 配置卡片
    .config-card {
      padding: 12px;
      margin-bottom: 10px;
      background: #fff;
      border-radius: 8px;
    }

    // 分区小标题（与消息序列编辑器弹窗同视觉）
    .section-title {
      display: flex;
      align-items: center;
      margin-bottom: 10px;
      font-size: 13px;
      font-weight: bold;
      color: #333;

      .title-bar {
        width: 3px;
        height: 14px;
        margin-right: 6px;
        border-radius: 2px;
        background: #67c23a;
      }

      .title-hint {
        margin-left: 8px;
        font-size: 12px;
        font-weight: normal;
        color: #999;
      }

      .edit-script-link {
        margin-left: auto;
        display: inline-flex;
        align-items: center;
        font-size: 12px;
        font-weight: normal;
        color: #409EFF;
        cursor: pointer;

        i {
          margin-right: 3px;
          font-size: 12px;
        }

        &:hover {
          color: #66b1ff;
        }
      }
    }

    // 触发条件行
    .condition-line {
      display: flex;
      align-items: center;
      flex-wrap: wrap;
      gap: 6px;
      font-size: 13px;
      color: #333;

      .interval-input {
        width: 110px;
      }
    }

    // 消息序列摘要
    .msg-list-box {
      .msg-row {
        display: flex;
        align-items: center;
        padding: 7px 10px;
        margin-bottom: 6px;
        background: #f7f8fa;
        border-radius: 8px;

        &:last-child {
          margin-bottom: 0;
        }

        .msg-seq {
          width: 18px;
          height: 18px;
          line-height: 18px;
          text-align: center;
          font-size: 11px;
          font-weight: 600;
          color: #67c23a;
          background: #f0f9eb;
          border-radius: 50%;
          flex-shrink: 0;
        }

        .msg-type-icon {
          margin: 0 8px;
          color: #67c23a;
          font-size: 15px;
          flex-shrink: 0;
        }

        .msg-preview {
          flex: 1;
          min-width: 0;
          font-size: 13px;
          color: #333;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }
      }

      .msg-empty {
        padding: 18px 16px;
        text-align: center;
        font-size: 12px;
        color: #999;
        background: #fafbfc;
        border: 1px dashed #e4e7ed;
        border-radius: 8px;

        i {
          display: block;
          margin-bottom: 6px;
          font-size: 24px;
          color: #c0c4cc;
        }

        .empty-text {
          margin-bottom: 10px;
        }
      }
    }

    // 操作区：启用开关 + 保存
    .followup-actions {
      display: flex;
      align-items: center;
      justify-content: space-between;
      padding: 2px 2px 0;

      .enable-switch {
        display: flex;
        align-items: center;
        font-size: 13px;
        color: #333;

        .enable-label {
          margin-right: 8px;
        }
      }

      .el-button {
        min-width: 88px;
      }
    }

    // 未保存提示
    .dirty-tip {
      margin-top: 8px;
      padding-left: 2px;
      font-size: 12px;
      color: #e6a23c;

      i {
        margin-right: 4px;
      }
    }
  }
}
</style>
